import { Injectable } from '@angular/core';
import { Task, TaskStatus } from '@tasks/tasks.types';
import { ApprovalStatus } from '@shared/modules/approvals/approvals.types';

export const TASK_WORKFLOW_NA: TaskStatus = {
  key: 'n_a',
  label: 'N/A',
  color: '#eaeaea',
  borderColor: '#454447',
  icon: 'ts_users-alt',
};
export const TASK_WORKFLOW_NOT_STARTED: TaskStatus = {
  key: 'not_started',
  label: 'Not Started',
  color: '#eaeaea',
  borderColor: '#454447',
  icon: 'ts_circle-none',
};
export const TASK_WORKFLOW_NEEDS_REVIEW: TaskStatus = {
  key: 'needs_review',
  label: 'Needs Review',
  color: '#fde3aa',
  borderColor: '#8e5b00',
  icon: 'ts_triangle-warn',
};
export const TASK_WORKFLOW_IN_PROGRESS: TaskStatus = {
  key: 'in_progress',
  label: 'In Progress',
  color: '#c1f0fe',
  borderColor: '#0071a4',
  icon: 'ts_progress',
};
export const TASK_WORKFLOW_IN_APPROVAL: TaskStatus = {
  key: 'in_approval',
  label: 'In Approval',
  color: '#f4daff',
  borderColor: '#83388A',
  icon: 'ts_user-check',
};
export const TASK_WORKFLOW_COMPLETE: TaskStatus = {
  key: 'complete',
  label: 'Complete',
  color: '#c3f6e0',
  borderColor: '#007a5a',
  icon: 'ts_check-circle-true',
};

export const TASK_WORKFLOW_STATUSES: TaskStatus[] = [
  TASK_WORKFLOW_NA,
  TASK_WORKFLOW_NOT_STARTED,
  TASK_WORKFLOW_NEEDS_REVIEW,
  TASK_WORKFLOW_IN_PROGRESS,
  TASK_WORKFLOW_IN_APPROVAL,
  TASK_WORKFLOW_COMPLETE,
];

@Injectable({
  providedIn: 'root',
})
export class TaskStatusService {
  constructor() {}

  getNextStatus(task: Task, permission = false) {
    switch (task.workflow_state) {
      case TASK_WORKFLOW_NA.key:
      case TASK_WORKFLOW_NOT_STARTED.key:
        return 'in_progress';
      case TASK_WORKFLOW_IN_PROGRESS.key:
      case TASK_WORKFLOW_NEEDS_REVIEW.key:
        if (task.approvals?.length > 0) {
          return TASK_WORKFLOW_IN_APPROVAL.key;
        } else {
          return TASK_WORKFLOW_COMPLETE.key;
        }
      case TASK_WORKFLOW_IN_APPROVAL.key:
        if (!task.approvals || task.approvals?.length <= 0) {
          return TASK_WORKFLOW_COMPLETE.key;
        }
        const approved = task.approvals?.every((a) => a.status === ApprovalStatus.APPROVED);
        if (approved) {
          return TASK_WORKFLOW_COMPLETE.key;
        } else {
          return TASK_WORKFLOW_IN_APPROVAL.key;
        }
      default:
        return task.workflow_state;
    }
  }

  getNextButtonLabel(task: Task, permission = false) {
    switch (task.workflow_state) {
      case TASK_WORKFLOW_NA.key:
      case TASK_WORKFLOW_NOT_STARTED.key:
        return 'Start Task';
      case TASK_WORKFLOW_IN_PROGRESS.key:
      case TASK_WORKFLOW_NEEDS_REVIEW.key:
        if (task.approvals?.length > 0) {
          return 'Request Approval';
        } else {
          return 'Complete';
        }
      case TASK_WORKFLOW_IN_APPROVAL.key:
        if (!task.approvals || task.approvals?.length <= 0) {
          return 'Complete';
        }

        const approved = task.approvals?.every((a) => a.status === ApprovalStatus.APPROVED);
        if (approved) {
          return 'Complete';
        } else {
          return 'Request Approval';
        }
      default:
        return 'Complete';
    }
  }

  getAvailableWorkflowStates(task?: Task): TaskStatus[] {
    if (!task) {
      return TASK_WORKFLOW_STATUSES;
    }

    return TASK_WORKFLOW_STATUSES.map((state: TaskStatus) => this.validateTaskState(task, state));
  }

  private validateTaskState(task: Task, workflowState): TaskStatus {
    // Disabling COMPLETE status if there are approvals and not all are approved
    if (
      workflowState.key === TASK_WORKFLOW_COMPLETE.key &&
      task.approvals?.length > 0 &&
      task.approvals?.some((a) => a.status !== ApprovalStatus.APPROVED)
    ) {
      return {
        ...workflowState,
        disabled: true,
        disabledReason: 'All stakeholders must approve the task in order to complete the task.',
      };
    }

    return { ...workflowState, disabled: false };
  }
}
